import { KeyboardSensor } from "@dnd-kit/core";
import type { KeyboardSensorProps, SensorInstance } from "@dnd-kit/core";

const ORIGIN = { x: 0, y: 0 };

const NAVIGATION_CODES = [
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
];

type Teardown = () => void;

export class ReorderKeyboardSensor implements SensorInstance {
  static activators = KeyboardSensor.activators;

  autoScrollEnabled = false;
  private props: KeyboardSensorProps;
  private referenceCoordinates: { x: number; y: number } | undefined;
  private teardowns: Teardown[] = [];

  constructor(props: KeyboardSensorProps) {
    this.props = props;
    const target = props.event.target as Node | null;
    const ownerDocument = target?.ownerDocument ?? document;
    const ownerWindow = ownerDocument.defaultView ?? window;

    props.onStart(ORIGIN);

    ownerWindow.addEventListener("resize", this.handleCancel);
    ownerWindow.addEventListener("visibilitychange", this.handleCancel);
    this.teardowns.push(
      () => ownerWindow.removeEventListener("resize", this.handleCancel),
      () =>
        ownerWindow.removeEventListener("visibilitychange", this.handleCancel),
    );

    const activator = props.activeNode.activatorNode.current;
    if (activator) {
      activator.addEventListener("blur", this.handleCancel);
      this.teardowns.push(() =>
        activator.removeEventListener("blur", this.handleCancel),
      );
    }

    const timer = setTimeout(() => {
      ownerDocument.addEventListener("keydown", this.handleKeyDown);
      this.teardowns.push(() =>
        ownerDocument.removeEventListener("keydown", this.handleKeyDown),
      );
    });
    this.teardowns.push(() => clearTimeout(timer));
  }

  private handleKeyDown = (event: Event) => {
    if (!(event instanceof KeyboardEvent)) return;
    const { active, context, options, onMove } = this.props;
    const { keyboardCodes, coordinateGetter } = options;
    if (keyboardCodes?.end.includes(event.code as never)) {
      this.finish(event, this.props.onEnd);
      return;
    }
    if (keyboardCodes?.cancel.includes(event.code as never)) {
      this.finish(event, this.props.onCancel);
      return;
    }

    if (NAVIGATION_CODES.includes(event.code)) event.preventDefault();

    const { collisionRect } = context.current;
    const currentCoordinates = collisionRect
      ? { x: collisionRect.left, y: collisionRect.top }
      : ORIGIN;
    this.referenceCoordinates ??= currentCoordinates;

    const next = coordinateGetter?.(event, {
      active,
      context: context.current,
      currentCoordinates,
    });
    if (!next) return;

    onMove({
      x: next.x - this.referenceCoordinates.x,
      y: next.y - this.referenceCoordinates.y,
    });
    requestAnimationFrame(() =>
      this.props.activeNode.node.current?.scrollIntoView?.({
        block: "nearest",
        inline: "nearest",
      }),
    );
  };

  private handleCancel = (event: Event) => {
    this.finish(event, this.props.onCancel);
  };

  private finish(event: Event, notify: () => void) {
    event.preventDefault();
    this.teardowns.forEach((teardown) => teardown());
    this.teardowns = [];
    notify();
  }
}
